/**
 * Data hooks of the PC sheet (2.H deliverable 4). Reads only: `docs.meta` and `types.list` (the
 * keys 1.AC-2's stat-block form uses, so the two pills share one request), `sheets.character.list`
 * (key `['character-sheets', docId]`), `sheets.character.get` and, for the empty state only,
 * `sheets.list`. `doc.changed` of the document refetches the list and the open sheet.
 */
import {
  ApiError,
  type CharacterSheetV1,
  type DocMetaV1,
  type DocTypeSummaryV1,
  docsMeta,
  sheetsCharacterGet,
  sheetsCharacterList,
  sheetsList,
  typesList,
} from '@codex/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../../../auth/client.js';
import { appEvents } from '../../../editor-services/events.js';

export type LoadFailure = 'missing' | 'failed';

/** A 404 is `missing`, anything else `failed`. */
export function failureOf(error: unknown): LoadFailure {
  return error instanceof ApiError && error.status === 404 ? 'missing' : 'failed';
}

export const metaKey = (docId: string) => ['meta', docId] as const;
export const typesKey = ['types'] as const;
export const characterSheetsKey = (docId: string) => ['character-sheets', docId] as const;
export const characterSheetKey = (sheetId: string) => ['character-sheet', sheetId] as const;
export const statBlocksKey = (docId: string) => ['sheets', docId] as const;

export interface CharacterDoc {
  readonly doc: DocMetaV1 | undefined;
  readonly type: DocTypeSummaryV1 | undefined;
  /** A canon document whose type has `has_sheet` (live or trashed). */
  readonly sheeted: boolean;
  readonly loading: boolean;
  readonly failure: LoadFailure | null;
  readonly retry: () => void;
}

export function useCharacterDoc(docId: string, enabled: boolean): CharacterDoc {
  const meta = useQuery({
    queryKey: metaKey(docId),
    queryFn: () => api(docsMeta, { params: { id: docId } }),
    enabled,
    retry: false,
  });
  const types = useQuery({
    queryKey: typesKey,
    queryFn: async () => (await api(typesList)).items,
    enabled,
    retry: false,
  });
  const doc = meta.data;
  const type = doc ? types.data?.find((t) => t.id === doc.typeId) : undefined;
  const error = meta.isError ? meta.error : types.isError ? types.error : null;
  return {
    doc,
    type,
    sheeted:
      doc !== undefined &&
      doc.library === 'canon' &&
      type?.capabilities.includes('has_sheet') === true,
    loading: enabled && error === null && (doc === undefined || types.data === undefined),
    failure: error === null ? null : failureOf(error),
    retry: () => {
      void meta.refetch();
      void types.refetch();
    },
  };
}

function useRefetchOnChange(docId: string, enabled: boolean, keys: readonly (readonly string[])[]) {
  const client = useQueryClient();
  const joined = JSON.stringify(keys);
  useEffect(() => {
    if (!enabled) return;
    return appEvents.subscribeDocChanged(docId, () => {
      for (const queryKey of JSON.parse(joined) as string[][]) {
        void client.invalidateQueries({ queryKey });
      }
    });
  }, [client, docId, enabled, joined]);
}

/** The document's character sheets, the primary first. */
export function useCharacterSheets(docId: string, enabled: boolean) {
  useRefetchOnChange(docId, enabled, [characterSheetsKey(docId)]);
  return useQuery({
    queryKey: characterSheetsKey(docId),
    queryFn: async () => (await api(sheetsCharacterList, { params: { id: docId } })).items,
    enabled,
    retry: false,
  });
}

/** One character sheet; shows `initial` (its list row) while it loads. */
export function useCharacterSheet(
  docId: string,
  sheetId: string | null,
  initial: CharacterSheetV1 | undefined,
) {
  const enabled = sheetId !== null;
  useRefetchOnChange(docId, enabled, sheetId === null ? [] : [characterSheetKey(sheetId)]);
  return useQuery({
    queryKey: characterSheetKey(sheetId ?? ''),
    queryFn: () => api(sheetsCharacterGet, { params: { sheetId: sheetId as string } }),
    enabled,
    retry: false,
    ...(initial ? { placeholderData: initial } : {}),
  });
}

/** Whether the document holds stat-block sheets (the empty state's other sentence). */
export function useHasStatBlock(docId: string, enabled: boolean) {
  return useQuery({
    queryKey: statBlocksKey(docId),
    queryFn: async () => (await api(sheetsList, { params: { id: docId } })).items,
    enabled,
    retry: false,
    select: (items) => items.length > 0,
  });
}
